import type { TeamDto } from '../../api/project-teams';
import type { TaskProjectDto } from '../../api/tasks';

/**
 * /task 팀 스코프 (design-20260724-project-team-scope D-1).
 *
 * `프로젝트` 메뉴가 `/task` 를 파라미터 없이 열기 때문에(PR #254), "무엇을 기본으로 보여줄지"를
 * 규칙으로 고정한다. 규칙과 순수 계산만 담고 렌더·라우팅은 호출부(TaskPage)가 맡는다.
 *
 * ⚠ TaskPage 는 **Router 컨텍스트 없이도 렌더되는 계약**(기존 테스트)이라 여기서도
 *    react-router 를 쓰지 않는다 — 주소는 `window.location` 읽기 + `history.replaceState` 로만 다룬다.
 */

/** 스코프 — 팀 하나 / 무소속(개인) / 내 전체. */
export type TaskScope =
  | { kind: 'all' }
  | { kind: 'personal' }
  | { kind: 'team'; teamId: string };

export function scopeKey(scope: TaskScope): string {
  return scope.kind === 'team' ? `team:${scope.teamId}` : scope.kind;
}

export function parseScopeKey(raw: string | null): TaskScope | null {
  if (!raw) return null;
  if (raw === 'all') return { kind: 'all' };
  if (raw === 'personal') return { kind: 'personal' };
  const m = /^team:(\d{1,19})$/.exec(raw);
  return m ? { kind: 'team', teamId: m[1]! } : null;
}

/**
 * 초기 스코프 결정 (설계 D-1) — 위에서 먼저 맞는 것.
 *   1. `?project=` 가 가리키는 프로젝트의 팀
 *   2. `?team=`
 *   3. **내 팀 전체**(기본 — 2026-07-24 사용자 결정)
 *   4. 팀이 하나도 없으면 개인
 *
 * ⚠ '최근 본 팀 기억'은 두지 않는다(2026-07-24) — 기본값이 항상 '내 팀 전체' 여야 한다는 결정.
 *    기억을 두면 지난 선택이 기본값을 덮어써 사용자가 매번 다른 화면을 보게 된다.
 */
export function resolveInitialScope(args: {
  teams: TeamDto[];
  projects: TaskProjectDto[];
  queryTeamId?: string | null;
  queryProjectId?: string | null;
}): TaskScope {
  const { teams, projects, queryTeamId, queryProjectId } = args;
  const hasTeam = (id: string) => teams.some((t) => t.id === id);

  if (queryProjectId) {
    const p = projects.find((x) => x.id === queryProjectId);
    if (p) return p.teamId ? { kind: 'team', teamId: p.teamId } : { kind: 'personal' };
  }
  if (queryTeamId && hasTeam(queryTeamId)) return { kind: 'team', teamId: queryTeamId };


  // 소속 팀이 있으면 '내 팀 전체' 가 기본 — 특정 팀을 임의로 고르면 다른 팀 작업이 숨는다.
  if (teams.length > 0) return { kind: 'all' };

  return { kind: 'personal' };
}

/** 스코프에 속한 프로젝트만. */
export function projectsInScope(projects: TaskProjectDto[], scope: TaskScope): TaskProjectDto[] {
  if (scope.kind === 'all') return projects;
  if (scope.kind === 'personal') return projects.filter((p) => !p.teamId);
  return projects.filter((p) => p.teamId === scope.teamId);
}

/**
 * 스코프 안에서 선택할 프로젝트 — 현재 선택이 스코프에 남아 있으면 유지, 아니면 첫 항목.
 * (스코프를 바꿨을 때 엉뚱한 팀의 프로젝트가 열린 채로 남지 않게 한다.)
 */
export function pickProjectForScope(
  scoped: TaskProjectDto[],
  currentId: string | null,
): string | null {
  if (currentId && scoped.some((p) => p.id === currentId)) return currentId;
  return scoped[0]?.id ?? null;
}

/** 스코프 표시명 — 팀명 / '개인' / '내 팀 전체'. 팀이 사라졌으면 null. */
export function scopeLabel(scope: TaskScope, teams: TeamDto[], labels: {
  all: string; personal: string;
}): string | null {
  if (scope.kind === 'all') return labels.all;
  if (scope.kind === 'personal') return labels.personal;
  return teams.find((t) => t.id === scope.teamId)?.name ?? null;
}

/**
 * 주소를 현재 상태에 맞춘다 — `/task?team=<id>&project=<id>`.
 * 히스토리를 쌓지 않고(replaceState) 새로고침·링크 공유만 보존한다.
 */
export function syncScopeUrl(scope: TaskScope, projectId: string | null): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const params = new URLSearchParams(window.location.search);
  if (scope.kind === 'team') params.set('team', scope.teamId);
  else if (scope.kind === 'personal') params.set('team', 'personal');
  else params.delete('team');
  if (projectId) params.set('project', projectId);
  else params.delete('project');
  const qs = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
}

/** `?team=` 값을 스코프로 — 'personal' 도 허용. 그 외 형식은 무시. */
export function scopeFromQueryTeam(raw: string | null): TaskScope | null {
  if (!raw) return null;
  if (raw === 'personal') return { kind: 'personal' };
  return /^\d{1,19}$/.test(raw) ? { kind: 'team', teamId: raw } : null;
}
